import { App, AppState } from "@capacitor/app";
import { PluginListenerHandle } from "@capacitor/core";
import { useEffect, useRef } from "react";

import { usePlatform } from "hooks/usePlatform";
import { usePlatformDomainTokens } from "hooks/usePlatformDomainTokens";
import { useUser } from "modules/user";
import { isAndroidPlatform, isIosPlatform } from "utils/platform.helpers";

import {
  changeAppIcon,
  convertSubDomainToLauncherIcon,
  getLauncherIconsFromPlatformDomainTokens,
} from "../helpers/appIcon.helpers";

// Listens for changes to the active (logged in) platform, and updates the app icon.
export default function AppIconListener(): null {
  const listenerRef = useRef<PluginListenerHandle | undefined>(undefined);

  const { user } = useUser();

  const { platform } = usePlatform();

  const { platformDomainTokens } = usePlatformDomainTokens();

  const subDomain = platform?.subdomain || "";

  const allLauncherIcons = getLauncherIconsFromPlatformDomainTokens(platformDomainTokens);

  const launcherIcon = convertSubDomainToLauncherIcon(subDomain);

  const iconsToDisable = allLauncherIcons.filter((i) => i != launcherIcon);

  // Android - runs in background via listener
  // We specifically listen for "appStateChange" vs "pause", because
  // permission requests (ie notifications) trigger a pause state, and the app
  // may crash if we attempt to change the icon at that point
  useEffect(() => {
    const setupListener = async () => {
      // Clean up previous listener if it exists
      if (listenerRef.current) {
        listenerRef.current.remove();
      }

      listenerRef.current = await App.addListener(
        "appStateChange",
        async ({ isActive }: AppState) => {
          if (!isActive && user?.id && launcherIcon) {
            changeAppIcon(launcherIcon, iconsToDisable);
          }
        },
      );
    };

    if (isAndroidPlatform() && user?.id && launcherIcon) {
      setupListener();
    }

    return () => {
      if (listenerRef.current) {
        listenerRef.current.remove();
      }
    };
  }, [iconsToDisable, launcherIcon, platform?.id, user?.id]);

  // iOS - runs in foreground
  useEffect(() => {
    if (isIosPlatform() && launcherIcon && user?.id) {
      changeAppIcon(launcherIcon, []);
    }
  }, [launcherIcon, user?.id]);

  return null;
}
